/**
 * Gift with Purchase - Simple PDP Implementation
 * Automatically adds configured gift items when products are added to cart from PDP
 */

class GiftWithPurchase {
  constructor() {
    this.sessionStorageKey = 'gwp_added_gifts'; 
    this.addedGifts = {}; 
    this.init();
  }

  init() {

    document.body.addEventListener('shapes:cart:afteradditem', this.handleCartAddition.bind(this));

    document.body.addEventListener('shapes:cart:cartqtychange', this.handleCartQuantityChange.bind(this)); 

    ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach((eventName) => {
      // window: some add-to-cart flows (e.g. the cart upsell) dispatch on window, not on body
      window.addEventListener(eventName, () => this.scheduleTierGiftSync());
    });
    this.scheduleTierGiftSync();

    this.registerProductOnPage();
  }

  /**
   * Register product from data attribute
   */
  registerProductOnPage() {
    const productRoot = document.querySelector('[data-product-root][data-gift]');

    if (productRoot) {
      try {
        const giftDataAttr = productRoot.getAttribute('data-gift');
        const giftData = JSON.parse(giftDataAttr);

        this.currentPageGift = giftData;

      } catch (error) {
        console.error("Failed to parse gift data:", error);
      }
    }
  }
  
  /**
   * Handle cart addition events
   */
  async handleCartAddition(event) {
    const { response } = event.detail;

    if (response.status || response.error) {
      return;
    }

    const addedItems = response.items || [response];

    for (const item of addedItems) {
      if (item.properties && item.properties._gift) {
        continue;
      }

      const productId = item.product_id;

      if (this.currentPageGift && this.currentPageGift.productId === productId) {
        await this.addGift(this.currentPageGift);
      } else {
      }
    }
  }

  /**
   * Add a gift to the cart (FIXED: Allows adding a gift every time the main product is added)
   */
  async addGift(giftData, extraProperties = {}) {
    if (typeof fetchConfigDefaults !== 'function') {
      this.notifyGiftError('Internal error: Cannot communicate with cart API.');
      return;
    }

    const { productId, giftVariantId } = giftData;

    try {
      const formData = new FormData();
      formData.append('id', giftVariantId);
      formData.append('quantity', '1');
      formData.append('properties[_gift]', 'Free Gift');
      formData.append('properties[_gift_for_product]', productId);
      Object.entries(extraProperties).forEach(([name, value]) => {
        formData.append(`properties[${name}]`, value);
      });

      // Section rendering
      formData.append('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfigDefaults();
      config.method = 'POST';
      config.body = formData;

      // Must remove Content-Type when sending FormData
      if (config.headers) {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
      }

      const response = await fetch(window.theme.routes.cart_add_url, config);

      if (!response.ok && response.status !== 422) {
        this.notifyGiftError('The free gift item returned an unexpected error or redirect. Check item availability.');
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch {
        this.notifyGiftError('Gift was likely not added due to a system redirection/error.');
        return;
      }

      if (data.status || data.error) {
        this.notifyGiftError(data.description || 'Gift item is currently unavailable');
        return;
      }

      // Update UI
      await this.refreshCartUI(data);

    } catch (err) {
      console.error('General Error adding gift:', err);
      this.notifyGiftError('Unexpected system error adding gift.');
    }
  }


  /**
   * Debounced entry point for the Tier 2 (cart progress bar) free gift sync
   */
  scheduleTierGiftSync() {
    clearTimeout(this.tierSyncTimer);
    this.tierSyncTimer = setTimeout(() => this.syncTierGift(), 150);
  }

  /**
   * Keeps the Tier 2 free gift in the cart in sync with the subtotal:
   * adds it when the threshold is reached, removes it when the subtotal drops
   * below it, and keeps its quantity at 1. The gift itself never counts
   * towards the threshold.
   */
  async syncTierGift() {
    if (this.tierSyncing) {
      this.tierSyncPending = true;
      return;
    }

    const config = document.querySelector('[data-tier-gwp]');
    if (!config) {
      // The cart drawer content may not be in the DOM yet on first load
      this.tierConfigRetries = (this.tierConfigRetries || 0) + 1;
      if (this.tierConfigRetries <= 10) {
        clearTimeout(this.tierSyncTimer);
        this.tierSyncTimer = setTimeout(() => this.syncTierGift(), 500);
      }
      return;
    }
    this.tierConfigRetries = 0;

    const variantId = Number(config.dataset.variantId);
    const threshold = Number(config.dataset.threshold);
    const available = config.dataset.available === 'true';
    // Only present when an eligible collection is set; otherwise every line counts
    const eligibleIds = config.dataset.eligibleProductIds === undefined
      ? null
      : config.dataset.eligibleProductIds.split(',').filter(Boolean).map(Number);

    this.tierSyncing = true;

    try {
      const cart = await this.getCartContents();
      if (!cart) return;

      const giftLines = cart.items.filter((item) => item.properties && item.properties._tier_gwp);
      const subtotal = cart.items
        .filter((item) => !(item.properties && item.properties._tier_gwp))
        .filter((item) => eligibleIds === null || eligibleIds.includes(item.product_id))
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifies = subtotal >= threshold;

      if (qualifies && available) {
        if (giftLines.length === 0) {
          await this.addGift(
            { productId: 'tier_2', giftVariantId: variantId },
            { _tier_gwp: 'true' }
          );
        } else {
          const [mainLine, ...extraLines] = giftLines;
          for (const line of extraLines) {
            await this.updateGiftQuantity(line.key, 0);
          }
          if (mainLine.quantity !== 1) {
            await this.updateGiftQuantity(mainLine.key, 1);
          }
        }
      } else {
        for (const line of giftLines) {
          await this.updateGiftQuantity(line.key, 0);
        }
      }
    } catch (error) {
      console.error('Error syncing tier 2 gift:', error);
    } finally {
      this.tierSyncing = false;
      if (this.tierSyncPending) {
        this.tierSyncPending = false;
        this.scheduleTierGiftSync();
      }
    }
  }

  /**
   * Check if a gift variant is already in the cart
   */
  async isGiftAlreadyInCart(giftVariantId) {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}.js`);
      if (!response.ok) return false;

      const { items } = await response.json();

      return items.some(item =>
        item.variant_id === giftVariantId &&
        item.properties?._gift === 'Free Gift'
      );

    } catch {
      return false;
    }
  }


  /**
   * Refresh cart UI after adding gift (Using fallback fetch if sections are missing)
   */
  async refreshCartUI(giftAddResponse) {

    try {
      let sectionsData = giftAddResponse.sections || {};
      
      if (Object.keys(sectionsData).length === 0 || !sectionsData['cart-items']) {
          
          const cartRefreshUrl = `${window.theme.routes.cart_url}?sections=cart-items,cart-footer,cart-item-count,cart-live-region&sections_url=${window.location.pathname}`;
          
          const cartRefreshResponse = await fetch(cartRefreshUrl);
          const cartRefreshData = await cartRefreshResponse.json();
          sectionsData = cartRefreshData.sections || {};
          
          if (Object.keys(sectionsData).length === 0) {
              return;
          }
      }
      

      document.body.dispatchEvent(new CustomEvent('shapes:cart:update', {
        bubbles: true,
        detail: {
          response: {
            sections: sectionsData
          }
        }
      }));

      document.body.dispatchEvent(new CustomEvent('shapes:cart:cartqtychange', {
        bubbles: true,
        detail: {
          response: {
            sections: sectionsData 
          }
        }
      }));

    } catch (error) {
      console.error('Error during final UI refresh step:', error);
    }
  }

  /**
   * Handle quantity change events triggered by the theme after a successful update.
   */
  async handleCartQuantityChange(event) {
    const { key, quantity, previousQuantity, response } = event.detail;
    const itemRoot = document.querySelector(`[data-line-item-key="${key}"]`);

    if (!itemRoot) return;

    const isGift = itemRoot.querySelector('[data-variant-id]').hasAttribute('data-gift');
    if (isGift || (itemRoot.querySelector('[data-product-title] p') && itemRoot.querySelector('[data-product-title] p').textContent.includes('Free Gift'))) {
        return;
    }
    
    if (itemRoot.getAttribute('data-gift-trigger') !== 'true') {
        return;
    }

    let giftData;
    try {
        giftData = JSON.parse(itemRoot.getAttribute('data-gift'));
    } catch (e) {
        return;
    }
    
    const delta = Number(quantity) - Number(previousQuantity);

    if (delta === 0) {
        return; 
    }

    const cart = response;
    
    const giftItem = cart.items.find(item => 
        item.variant_id == giftData.giftVariantId &&
        item.properties && 
        item.properties._gift === 'Free Gift'
    );
    
    if (!giftItem) {
        return;
    }
    
    let currentGiftQty = giftItem.quantity;
    let targetGiftQty = currentGiftQty + delta;

    if (delta !== 0) {
        
        await this.updateGiftQuantity(giftItem.key, targetGiftQty);
    }
  }

  /**
   * Utility to fetch current cart contents (REQUIRED FOR RELIABLE QUANTITY TRACKING)
   */
  async getCartContents() {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}.js`);
      return await response.json();
    } catch (error) {
      return null;
    }
  }
  
  /**
  * Utility to update a line item quantity via the cart/change endpoint
  */
  async updateGiftQuantity(lineItemKey, quantity) {
    const config = fetchConfigDefaults(); 
    
    const formData = new FormData();
    formData.append('id', lineItemKey); 
    formData.append('quantity', quantity);

    formData.append('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
    formData.append('sections_url', window.location.pathname);
    
    config.body = new URLSearchParams(formData);
    config.method = 'POST';
    config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    config.headers['X-Requested-With'] = 'XMLHttpRequest'; 
    
    try {
      const response = await fetch(window.theme.routes.cart_change_url, config);
      const data = await response.json();
      
      if (data.status || data.error) {
        this.notifyGiftError(data.description || 'Could not update gift item quantity.');
        return;
      }
      
      await this.refreshCartUI(data); 

    } catch (error) {
      console.error(`General Error updating gift quantity:`, error);
    }
  }

  /**
   * Notify user of gift-related errors
   */
  notifyGiftError(message) {
    document.dispatchEvent(new CustomEvent('theme:cart:error', {
      bubbles: true,
      detail: {
        message: `Free gift: ${message}`,
        source: 'gift-with-purchase'
      }
    }));
  }

  /**
   * These methods are largely redundant now but kept for completeness
   */
  async syncGiftTracking() {
    
  }
  clearTracking() {
    sessionStorage.removeItem(this.sessionStorageKey);
  }
  getTrackingState() {
    return {
      addedGifts: this.addedGifts,
      currentPageGift: this.currentPageGift || null
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.giftWithPurchase = new GiftWithPurchase();
  });
} else {
  window.giftWithPurchase = new GiftWithPurchase();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GiftWithPurchase();
}